import { Children, isValidElement } from 'react';
import type { ReactNode } from 'react';
import type { QueryClient } from '@tanstack/react-query';
import { matchRoutes } from 'react-router-dom';
import type { Location, Params, RouteMatch, createBrowserRouter } from 'react-router-dom';

import { meAccessFrom } from '../access/api/normalizeMe';
import { ACCESS_POLICY } from '../access/policy';
import type { AccessPolicy } from '../access/policy';
import { meAccessQueryOptions } from '../access/queryOptions';
import { decideBranch } from '../access/routing/routeAccess';
import { sessionQueryOptions } from '../session/queryOptions';

export interface WarmUpContext {
  readonly queryClient: QueryClient;
  readonly params: Params;
  readonly searchParams: URLSearchParams;
}

export type WarmUp = (context: WarmUpContext) => void;

export const warmUp = (fn: WarmUp): { readonly warmUp: WarmUp } => ({ warmUp: fn });

const warmUpOf = (handle: unknown): WarmUp | null => {
  const fn = typeof handle === 'object' && handle !== null ? (handle as { warmUp?: unknown }).warmUp : undefined;
  return typeof fn === 'function' ? (fn as WarmUp) : null;
};

interface LazyPageType {
  readonly preload: () => Promise<void>;
}

const isLazyPage = (type: unknown): type is LazyPageType =>
  typeof type === 'function' && typeof (type as { preload?: unknown }).preload === 'function';

const MAX_DEPTH = 3;

const lazyPagesIn = (node: ReactNode, depth = 0): LazyPageType[] => {
  if (depth > MAX_DEPTH || !isValidElement(node)) return [];
  const own = isLazyPage(node.type) ? [node.type] : [];
  const { children } = node.props as { children?: ReactNode };
  return [...own, ...Children.toArray(children).flatMap((child) => lazyPagesIn(child, depth + 1))];
};

const preloadCode = (matches: readonly RouteMatch[]): void => {
  matches.forEach((match) => lazyPagesIn(match.route.element).forEach((page) => void page.preload()));
};

const warmData = async (queryClient: QueryClient, matches: readonly RouteMatch[], searchParams: URLSearchParams, policy: AccessPolicy) => {
  const warmUps = matches.flatMap((match) => {
    const fn = warmUpOf(match.route.handle);
    return fn === null ? [] : [{ fn, params: match.params }];
  });
  if (warmUps.length === 0) return;
  try {
    if ((await queryClient.ensureQueryData(sessionQueryOptions())).status !== 'signedIn') return;
    const me = meAccessFrom(await queryClient.ensureQueryData(meAccessQueryOptions()));
    const branch = matches.map((match) => ({ handle: match.route.handle, params: match.params }));
    if (!decideBranch(me, branch, searchParams).decision.allow && policy === 'enforce') return;
  } catch {
    return;
  }
  warmUps.forEach(({ fn, params }) => fn({ queryClient, params, searchParams }));
};

type AppRouter = ReturnType<typeof createBrowserRouter>;

export const warmRoutes = (router: Pick<AppRouter, 'routes' | 'state' | 'subscribe'>, queryClient: QueryClient, policy: AccessPolicy = ACCESS_POLICY): (() => void) => {
  let warmed: string | null = null;
  const warm = (location: Location): void => {
    const address = `${location.pathname}${location.search}`;
    if (address === warmed) return;
    warmed = address;
    const matches = matchRoutes(router.routes, location) ?? [];
    preloadCode(matches);
    void warmData(queryClient, matches, new URLSearchParams(location.search), policy);
  };
  warm(router.state.location);
  return router.subscribe((state) => warm(state.navigation.location ?? state.location));
};
